import { Build, MoreVert, NoteAdd, SwapHoriz } from '@mui/icons-material';
import React, { useRef, useState } from 'react';

import { FILE_UPLOAD_LIMITS, MobileActionBarProps } from './MobileActionBar.props';
import {
  ActionBarContainer,
  ActionButton,
  ButtonGrid,
  HiddenInput,
} from './MobileActionBar.styles';
import { MobileMoreMenuSheet } from './MobileMoreMenuSheet';
import { ActionPermissions } from '../../../../../@types';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { MobileAddNoteDialog } from '../MobileQuickActions/MobileAddNoteDialog';
import { MobilePartActionsDialog } from '../MobileQuickActions/MobilePartActionsDialog';
import { MobileStatusUpdateDialog } from '../MobileQuickActions/MobileStatusUpdateDialog';

const MB_IN_BYTES = 1024 * 1024;

export const MobileActionBar: React.FC<MobileActionBarProps> = ({
  service,
  nextStatuses,
  partActionTypes,
  permissions,
  onTakePhoto,
  onUploadFiles,
  onUploadError,
  onStatusChanged,
  onNoteAdded,
  onPartActionComplete,
}) => {
  const canChangeStatus = isUserHasPermission(
    ActionPermissions.Service_Edit_Status_Progress_Status_Change,
    permissions
  );
  const canAddNotes = isUserHasPermission(ActionPermissions.Service_Edit_Add_Notes, permissions);
  const canPartActions = isUserHasPermission(
    ActionPermissions.Service_Edit_Part_Actions,
    permissions
  );
  const [activeDialog, setActiveDialog] = useState<'more' | 'status' | 'note' | 'parts' | null>(
    null
  );
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTakePhotoClick = (): void => {
    cameraInputRef.current?.click();
  };

  const handleUploadFilesClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleCameraCapture = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const { maxFileSizeMB } = FILE_UPLOAD_LIMITS;
      if (file.size > maxFileSizeMB * MB_IN_BYTES) {
        onUploadError?.({
          type: 'fileSize',
          message: `Photo exceeds ${maxFileSizeMB}MB limit.`,
        });
        event.target.value = '';
        return;
      }
      onTakePhoto(file);
    }
    event.target.value = '';
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    const { maxFiles, maxFileSizeMB, maxTotalSizeMB } = FILE_UPLOAD_LIMITS;

    if (files.length > maxFiles) {
      onUploadError?.({
        type: 'maxFiles',
        message: `Maximum ${maxFiles} files allowed. You selected ${files.length}.`,
      });
      event.target.value = '';
      return;
    }

    const oversizedFile = files.find((f) => f.size > maxFileSizeMB * MB_IN_BYTES);
    if (oversizedFile) {
      onUploadError?.({
        type: 'fileSize',
        message: `File "${oversizedFile.name}" exceeds ${maxFileSizeMB}MB limit.`,
      });
      event.target.value = '';
      return;
    }

    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > maxTotalSizeMB * MB_IN_BYTES) {
      const totalMB = (totalSize / MB_IN_BYTES).toFixed(1);
      onUploadError?.({
        type: 'totalSize',
        message: `Total size ${totalMB}MB exceeds ${maxTotalSizeMB}MB limit.`,
      });
      event.target.value = '';
      return;
    }

    onUploadFiles(files);
    event.target.value = '';
  };

  return (
    <>
      <ActionBarContainer>
        <ButtonGrid>
          {canChangeStatus && (
            <ActionButton
              buttonVariant="primary"
              onClick={() => setActiveDialog('status')}
              aria-label="Update status"
            >
              <SwapHoriz />
              Status
            </ActionButton>
          )}
          {canPartActions && (
            <ActionButton
              buttonVariant="secondary"
              onClick={() => setActiveDialog('parts')}
              aria-label="Part actions"
            >
              <Build />
              Parts
            </ActionButton>
          )}
          {canAddNotes && (
            <ActionButton
              buttonVariant="secondary"
              onClick={() => setActiveDialog('note')}
              aria-label="Add note"
            >
              <NoteAdd />
              Note
            </ActionButton>
          )}
          <ActionButton
            buttonVariant="secondary"
            onClick={() => setActiveDialog('more')}
            aria-label="More options"
          >
            <MoreVert />
            More
          </ActionButton>
        </ButtonGrid>
      </ActionBarContainer>

      <HiddenInput
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        aria-label="Take photo"
      />

      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelection}
        aria-label="Upload files"
      />

      <MobileMoreMenuSheet
        open={activeDialog === 'more'}
        onClose={() => setActiveDialog(null)}
        onTakePhoto={handleTakePhotoClick}
        onUploadFiles={handleUploadFilesClick}
      />

      <MobileStatusUpdateDialog
        open={activeDialog === 'status'}
        onClose={() => setActiveDialog(null)}
        service={service}
        nextStatuses={nextStatuses}
        onStatusChanged={onStatusChanged}
      />

      <MobileAddNoteDialog
        open={activeDialog === 'note'}
        onClose={() => setActiveDialog(null)}
        serviceId={service.id}
        onNoteAdded={onNoteAdded}
      />

      <MobilePartActionsDialog
        open={activeDialog === 'parts'}
        onClose={() => setActiveDialog(null)}
        service={service}
        partActionTypes={partActionTypes}
        onPartActionComplete={onPartActionComplete}
      />
    </>
  );
};
